import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight, type LucideIcon } from "lucide-react";
import { cn } from "@weco/ui";
import { userCanAccessAdminApp } from "./adminAccess";
import type { SuiteChromeUser } from "./types";

export type SuiteChromeMode = "shell" | "remote";

export interface SidebarRoute {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
  requiresSuperAdmin?: boolean;
}

/** A titled section of the sidebar (design: the plan-tier groups). */
export interface SidebarGroup {
  id: string;
  /** Section heading, shown only while the sidebar is expanded. */
  label?: string;
  routes: ReadonlyArray<SidebarRoute>;
}

export interface SidebarLabels {
  expandSidebar?: string;
  collapseSidebar?: string;
  homeLabel?: string;
  navigationLabel?: string;
}

export interface SuiteSidebarProps {
  mode: SuiteChromeMode;
  currentAppId?: string;
  user?: SuiteChromeUser | null;
  brandingLogoUrl?: string;
  brandingAppName?: string;
  /** Grouped primary navigation. Empty groups are not rendered. */
  groups: ReadonlyArray<SidebarGroup>;
  /** Bottom section (All apps, Settings). */
  secondary: ReadonlyArray<SidebarRoute>;
  /** Route id to highlight; defaults to matching the current pathname. */
  activeRouteId?: string | null;
  isOpen: boolean;
  /** 768–1023px: the rail stays in flow and expanding overlays the workspace. */
  isTablet?: boolean;
  onToggle: () => void;
  onClose: () => void;
  onLogoClick?: () => void;
  sidebarLabels?: SidebarLabels;
  /** Route whose icon carries the pulsing "incoming call" dot. */
  ringingRouteId?: string | null;
}

/** Route id whose path owns `pathname` ("/" only for the root itself). */
function routeIdForPath(pathname: string, routes: ReadonlyArray<SidebarRoute>): string | null {
  const first = pathname.split("/").filter(Boolean)[0] ?? "";
  const match = routes.find((r) => r.path !== "/" && r.path === `/${first}`);
  if (match) return match.id;
  return first === "" ? (routes.find((r) => r.path === "/")?.id ?? null) : null;
}

interface Tip {
  label: string;
  market: boolean;
  top: number;
  x: number;
}

/**
 * The suite sidebar (design: `weco2/shell.jsx` Sidebar).
 *
 * Desktop (>=1024): pushes content, 64px rail <-> 256px expanded, starts
 * collapsed. Tablet (768-1023): the 64px rail stays in flow; expanding
 * overlays the workspace with a scrim (Escape closes it). Collapsed-rail
 * tooltips are viewport-fixed so the scrolling nav cannot clip them.
 *
 * `data-tour-id="sidebar-root"` is load-bearing twice over: the product tour
 * targets it, and tenant branding paints the sidebar colour through it.
 */
export const SuiteSidebar: React.FC<SuiteSidebarProps> = ({
  user,
  brandingLogoUrl,
  brandingAppName = "We-Co",
  groups,
  secondary,
  activeRouteId,
  isOpen,
  isTablet = false,
  onToggle,
  onClose,
  onLogoClick,
  sidebarLabels,
  ringingRouteId = null,
}) => {
  const {
    expandSidebar = "Expand sidebar",
    collapseSidebar = "Collapse sidebar",
    homeLabel = "Home",
    navigationLabel = "Primary navigation",
  } = sidebarLabels ?? {};
  const collapsed = !isOpen;
  const location = useLocation();
  const [tip, setTip] = useState<Tip | null>(null);
  const asideRef = useRef<HTMLElement>(null);
  const [anchor, setAnchor] = useState<{ top: number; start: number } | null>(null);
  const isRtl = typeof document !== "undefined" && document.documentElement.dir === "rtl";

  const allRoutes = [...groups.flatMap((g) => g.routes), ...secondary];
  const activeId = activeRouteId !== undefined ? activeRouteId : routeIdForPath(location.pathname, allRoutes);

  useEffect(() => {
    setTip(null);
  }, [collapsed, activeId]);

  useEffect(() => {
    if (!(isTablet && isOpen)) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isTablet, isOpen, onClose]);

  // The collapse toggle sits on the sidebar's inner edge. It is positioned
  // from the sidebar's measured start edge rather than living inside it, so a
  // theme that clips the sidebar (overflow: hidden on a rounded glass panel)
  // cannot cut it in half.
  useLayoutEffect(() => {
    const measure = () => {
      const el = asideRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const rtl = document.documentElement.dir === "rtl";
      setAnchor({ top: r.top, start: rtl ? window.innerWidth - r.right : r.left });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [isRtl, isTablet]);

  const showTip = (e: React.SyntheticEvent<HTMLElement>, label: string, market: boolean) => {
    if (!collapsed) return;
    const r = e.currentTarget.getBoundingClientRect();
    setTip({
      label,
      market,
      top: r.top + r.height / 2,
      x: isRtl ? window.innerWidth - r.left + 12 : r.right + 12,
    });
  };

  const item = (it: SidebarRoute) => {
    if (it.requiresSuperAdmin && !userCanAccessAdminApp(user ?? null)) return null;
    const isActive = activeId === it.id;
    const isMarket = it.id === "marketplace";
    const Icon = it.icon;
    return (
      <Link
        key={it.id}
        to={it.path}
        data-tour-id={`sidebar-${it.id}`}
        aria-label={collapsed ? it.label : undefined}
        aria-current={isActive ? "page" : undefined}
        onClick={() => {
          if (isTablet) onClose();
        }}
        onMouseEnter={(e) => showTip(e, it.label, isMarket)}
        onMouseLeave={() => setTip(null)}
        onFocus={(e) => showTip(e, it.label, isMarket)}
        onBlur={() => setTip(null)}
        className={cn(
          "w-full flex items-center h-9 rounded-xl transition-all group relative border border-transparent",
          collapsed ? "justify-center px-0" : "px-3 gap-3",
          isMarket
            ? "bg-foreground text-background font-bold shadow-lg hover:scale-[1.02] active:scale-[0.98]"
            : isActive
              ? "bg-primary/10 text-foreground font-bold"
              : "text-muted-foreground hover:bg-muted hover:text-foreground font-medium",
        )}
      >
        <span className="relative shrink-0">
          <Icon size={16} aria-hidden className={cn(isMarket && "text-background", isActive && !isMarket && "text-primary")} />
          {ringingRouteId === it.id && <span aria-hidden className="absolute -top-1.5 -right-1.5 w-2.5 h-2.5 bg-emerald-500 rounded-full ring-2 ring-background anim-pulse-rec" />}
        </span>
        {!collapsed && (
          <span data-i18n-skip="true" className="text-[12.5px] tracking-wide whitespace-nowrap truncate">
            {it.label}
          </span>
        )}
      </Link>
    );
  };

  const logoInner = (
    <>
      {brandingLogoUrl ? (
        <img
          src={brandingLogoUrl}
          alt={brandingAppName}
          className="w-9 h-9 rounded-xl object-cover shrink-0 group-hover:scale-105 transition-transform"
        />
      ) : (
        <div className="w-9 h-9 bg-primary rounded-xl flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
          <span className="font-black text-lg text-primary-foreground lowercase leading-none">w</span>
        </div>
      )}
      {!collapsed && (
        <span className="flex items-baseline gap-2 min-w-0">
          <span className="font-black text-xl tracking-tight text-foreground truncate">{brandingAppName}.</span>
          <span className="shrink-0 rounded-full px-1.5 py-px text-[10px] font-bold uppercase tracking-[0.06em] leading-4 bg-blue-500/10 text-blue-600 ring-1 ring-inset ring-blue-500/25 dark:bg-blue-400/15 dark:text-blue-300 dark:ring-blue-400/30">
            Beta
          </span>
        </span>
      )}
    </>
  );
  const logoClass = cn("flex items-center mb-5 shrink-0 group h-9", collapsed ? "justify-center px-0" : "gap-3 px-5");

  const aside = (
    <aside
      ref={asideRef}
      className={cn(
        "transition-all duration-300 ease-in-out workspace-strip flex flex-col py-4",
        isTablet ? "absolute inset-y-0 start-0 z-50" : "relative z-50 shrink-0",
        isOpen ? "w-64" : "w-16",
        isTablet && isOpen && "shadow-2xl",
      )}
      style={{
        borderStartStartRadius: 16,
        borderInlineEnd: "1px solid color-mix(in srgb, var(--color-foreground) 6%, transparent)",
      }}
      aria-label={`${brandingAppName} navigation`}
      data-tour-id="sidebar-root"
      data-collapsed={collapsed ? "true" : "false"}
    >
      {onLogoClick ? (
        <button type="button" onClick={onLogoClick} title={homeLabel} aria-label={`${brandingAppName} home`} className={logoClass}>
          {logoInner}
        </button>
      ) : (
        <Link to="/" title={homeLabel} aria-label={`${brandingAppName} home`} className={logoClass}>
          {logoInner}
        </Link>
      )}
      <nav aria-label={navigationLabel} className="flex-1 space-y-2 overflow-y-auto no-scrollbar px-3">
        {groups.map((g) => {
          const visible = g.routes.filter((r) => !r.requiresSuperAdmin || userCanAccessAdminApp(user ?? null));
          if (!visible.length) return null;
          return (
            <div key={g.id} className="space-y-0.5" data-sidebar-group={g.id}>
              {!collapsed && g.label && (
                <p data-i18n-skip="true" className="px-4 text-[8px] font-black uppercase tracking-widest text-muted-foreground/60">
                  {g.label}
                </p>
              )}
              {visible.map(item)}
            </div>
          );
        })}
      </nav>
      {secondary.length > 0 && (
        <div className="pt-4 border-t border-border mt-4 px-3 space-y-2">{secondary.map(item)}</div>
      )}
    </aside>
  );

  return (
    <>
      {isTablet && isOpen && (
        <div onClick={onClose} className="fixed inset-0 bg-black/30 z-40" aria-hidden="true" />
      )}
      {isTablet ? <div className="relative shrink-0 w-16 z-50">{aside}</div> : aside}
      <button
        type="button"
        onClick={onToggle}
        aria-label={collapsed ? expandSidebar : collapseSidebar}
        aria-expanded={!collapsed}
        className="wc-sidebar-toggle fixed w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center border border-border shadow-lg z-[60] hover:scale-110 transition-[inset-inline-start,transform] duration-300"
        style={{
          top: (anchor?.top ?? 2) + 36,
          insetInlineStart: (anchor?.start ?? 2) + (collapsed ? 64 : 256) - 12,
        }}
      >
        {collapsed ? (
          <ChevronRight size={12} strokeWidth={3} className="rtl:rotate-180" aria-hidden />
        ) : (
          <ChevronLeft size={12} strokeWidth={3} className="rtl:rotate-180" aria-hidden />
        )}
      </button>
      {tip && (
        <div
          data-i18n-skip="true"
          role="tooltip"
          className={cn(
            "fixed z-[120] px-3 py-2 text-xs font-bold rounded-lg pointer-events-none whitespace-nowrap shadow-xl",
            tip.market ? "bg-primary text-primary-foreground" : "bg-foreground text-background",
          )}
          style={{ top: tip.top, [isRtl ? "right" : "left"]: tip.x, transform: "translateY(-50%)" }}
        >
          {tip.label}
        </div>
      )}
    </>
  );
};
